import { useMemo } from "react";
import { useView } from "../contexts/ViewContext";

/**
 * Active-org role (Multi-Org Membership — Epic 1).
 *
 * Role is per organization, not per user. The effective role follows the
 * currently selected org (owned by ViewContext, persisted to localStorage as
 * `view_selected_org_id`). When no org is selected — platform_admin comparing
 * across orgs, or before a default is chosen — the role falls back to the
 * user's highest-priority membership so the shell/nav still render. Org-scoped
 * admin controls must NOT gate on the fallback; they gate on the active-org
 * role, which is null/99 until a real selection exists, hiding admin controls
 * until then.
 *
 * Returns the numeric `role` (legacy 0/1/2/3 mapping) so existing
 * `role === 3` / `role <= 2` checks keep working after a one-line swap from
 * `Number(localStorage.getItem('role'))` to `useActiveRole().role`.
 *
 * `isPlatformAdmin` is a USER-level trait (sourced from the primary
 * membership), NOT org-scoped — a platform_admin sees every org.
 */
export function useActiveRole() {
  const view = useView();
  return useMemo(() => ({
    role: view.role,
    roleName: view.roleName,
    activeOrgId: view.activeOrgId,
    activeMembership: view.activeMembership,
    isPlatformAdmin: view.isPlatformAdmin,
    isOrgOwner: view.isOrgOwner,
    isOrgAdmin: view.isOrgAdmin,
    isAnalyst: view.isAnalyst,
    isClient: view.isClient,
    isStaff: view.isStaff,
    isSuperAdmin: view.isSuperAdmin,
    isManager: view.isManager,
    isAdmin: view.isAdmin,
    canSelectClients: view.canSelectClients,
    // Epic 2 (item 35 Q2) — additive gate: (isAdmin && !isStaff) || canOpenClientDashboard.
    canOpenClientDashboard: view.canOpenClientDashboard,
  }), [
    view.role, view.roleName, view.activeOrgId, view.activeMembership,
    view.isPlatformAdmin, view.isOrgOwner, view.isOrgAdmin, view.isAnalyst,
    view.isClient, view.isStaff, view.isSuperAdmin, view.isManager, view.isAdmin,
    view.canSelectClients,
    view.canOpenClientDashboard,
  ]);
}

export default useActiveRole;
